import _ from 'lodash';
import moment from 'moment';
import { useCallback } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from 'react-query';
import { storeGeneralSettings } from 'storage';
import { extractWithVal, formatUserFilterPayload } from 'utils';
import { useLoadFileBlobHelper } from 'utils/blobHelpers';
import { amApi } from '../api';
import { clearCache } from '../clearCache';
import { queryKeys } from '../keys';

export const useEmployees = (
  { canManageEmployee, page, search, status, params },
  options,
) => {
  const { data, ...rest } = useQuery(
    queryKeys.employees(canManageEmployee, page, search, status, params),
    async () =>
      canManageEmployee
        ? amApi.getManageEmployees({
            ...formatUserFilterPayload({ status, search, page }),
            ...params,
          })
        : amApi.getViewEmployee({
            ...formatUserFilterPayload({ status, search, page }),
            ...params,
          }),
    {
      ...options,
    },
  );

  return {
    employees: data?.data ?? [],
    pagination: data?.pagination ?? null,
    ...rest,
  };
};

export const useEmployee = ({ canManageEmployee, id }, options) => {
  const { data, ...rest } = useQuery(
    canManageEmployee ? queryKeys.employee(id) : queryKeys.employeeProfile(id),
    canManageEmployee
      ? async () => amApi.getEmployee({ userId: id })
      : async () => amApi.getEmployeeProfile({ userId: id }),
    {
      ...options,
    },
  );
  return { profile: data, ...rest };
};

export const useJobs = ({ page, search, ...payload }, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.jobs(page, search, payload),
    async () =>
      amApi.getJobs({
        ...formatUserFilterPayload({ search, page }),
        ...payload,
      }),
    {
      ...options,
    },
  );
  return {
    jobs: data?.data ?? [],
    pagination: data?.pagination ?? null,
    ...rest,
  };
};

export const useJob = ({ jobId, ...params }, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.job(jobId, params),
    async () => amApi.getJob(jobId, params),
    {
      ...options,
    },
  );
  return {
    jobDetails: data?.job ?? null,
    candidates: data?.candidates?.data,
    pagination: data?.candidates?.pagination ?? {},
    ...rest,
  };
};

export const useCandidates = (
  { page, search, sortBy = '', filter = {} },
  options,
) => {
  const { data, ...rest } = useQuery(
    queryKeys.candidates(page, search, sortBy, filter),
    async () =>
      amApi.getCandidates(
        formatUserFilterPayload({ page, search, sortBy, filter }),
      ),
    {
      ...options,
    },
  );
  return { candidates: data?.data, pagination: data?.pagination, ...rest };
};

export const useCandidate = (id, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.candidate(id),
    async () => amApi.getCandidate({ candidateId: id }),
    {
      ...options,
    },
  );
  return { candidateDetails: data, ...rest };
};

export const useTalentGroups = (options) => {
  const { data, ...rest } = useQuery(
    queryKeys.talentGroups(),
    async () => amApi.getTalentGroups(),
    {
      ...options,
    },
  );
  return { talentGroups: data, ...rest };
};

export const useTalentGroup = (id, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.talentGroup(id),
    async () => amApi.getTalentGroup({ talentGroupId: id }),
    {
      ...options,
    },
  );
  return { talentGroupDetails: data, ...rest };
};

export const useRequests = ({ canManageEmployee, ...params }, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.requests(
      params ? Object.values(params) : ['all'],
      canManageEmployee,
    ),
    canManageEmployee
      ? async () => amApi.getManageRequests(params)
      : async () => amApi.getEmployeeRequests(extractWithVal(params)),
    {
      ...options,
    },
  );
  return {
    requests: data?.data ?? [],
    pagination: data?.pagination ?? {},
    ...rest,
  };
};

export const useRequest = (id, options) => {
  const { data: details, ...rest } = useQuery(
    queryKeys.request(id),
    async () => amApi.getRequest({ requestId: id }),
    {
      ...options,
    },
  );
  return { details, ...rest };
};

export const useCurrentUser = (options) => {
  const { data: user, ...rest } = useQuery(
    queryKeys.user(),
    async () => amApi.getUser(),
    {
      ...options,
    },
  );
  return { user, ...rest };
};

export const useEmployeeSchema = (options) => {
  const { data: schema, ...rest } = useQuery(
    queryKeys.employeeSchema(),
    async () => amApi.getEmployeeSchema(),
    {
      ...options,
    },
  );
  return { schema, ...rest };
};

export const useNotes = ({ contentId, contentType }, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.notes(),
    async () =>
      amApi.getNotes({ content_id: contentId, content_type: contentType }),
    {
      ...options,
    },
  );
  return { notes: data ?? [], ...rest };
};

export const useLoadFileBlob = (fileId, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.fileBlob(fileId),
    async () => amApi.getFileBlob(fileId),
    {
      ...options,
    },
  );
  const url = useLoadFileBlobHelper(data?.data);

  return { file: data?.data ?? null, url, ...rest };
};

export const useFiles = (
  { canManageEmployee, contentId, contentType, parentId, userId, ...params },
  options,
) => {
  const { data, ...rest } = useQuery(
    queryKeys.files(
      contentId,
      contentType,
      parentId,
      userId,
      canManageEmployee,
      params,
    ),
    async () =>
      canManageEmployee
        ? amApi.getManageFiles({
            content_id: contentId,
            content_type: contentType,
            parent_id: parentId,
            user_id: userId,
            ...params,
          })
        : amApi.getEmployeeFiles({
            content_id: contentId,
            content_type: contentType,
            parent_id: parentId,
            user_id: userId,
          }),
    {
      ...options,
    },
  );

  return { files: data ?? [], ...rest };
};

export const usePushNotifications = (
  { status, userId, ...payload },
  options,
) => {
  const { data, ...rest } = useQuery(
    queryKeys.notifications(status, payload),
    async () => amApi.getNotifications(userId, { status, ...payload }),
    {
      ...options,
    },
  );
  const queryClient = useQueryClient();

  const invalidatedNotificationsList = useCallback(() => {
    clearCache.onChangeNotifications(queryClient);
  }, [queryClient]);

  const notifications = data?.data ?? [];
  if (typeof status === 'number') {
    return {
      notifications: notifications?.filter((item) => item.status === status),
      invalidatedNotificationsList,
      ...rest,
    };
  }
  return {
    notifications,
    invalidatedNotificationsList,
    pagination: data?.pagination,
    ...rest,
  };
};

export const useTrainings = (options) => {
  const { data, ...rest } = useQuery(
    queryKeys.trainings(),
    async () => amApi.getTrainings(),
    {
      ...options,
    },
  );
  return { trainings: data ?? [], ...rest };
};

export const useTrainingsCategories = (options, payload) => {
  const { data, ...rest } = useQuery(
    queryKeys.trainingsCategories(payload),
    async () => amApi.getTrainingCategories(payload),
    {
      ...options,
    },
  );
  return { categories: data ?? [], ...rest };
};

export const useTimeOffPolicies = (
  { employeeId, canManageEmployee },
  options,
) => {
  const { data, ...rest } = useQuery(
    queryKeys.timeOffPolicies(canManageEmployee, employeeId),
    async () => amApi.getTimeOffPolicies({ employeeId, canManageEmployee }),
    {
      ...options,
    },
  );

  return {
    ...rest,
    policies: (Array.isArray(data?.data) ? data?.data : data?.policies) || [],
    policyTypes: data?.types || {},
  };
};

export const useTimeOffCategories = ({ canManageEmployee }, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.timeOffCategories(canManageEmployee),
    canManageEmployee
      ? async () => amApi.getManageTimeOffCategories()
      : async () => amApi.getProfileTimeOffCategories(),
    {
      ...options,
    },
  );
  return { categories: data ?? [], ...rest };
};

export const useTimeOffRecords = (
  { canManageEmployee, ...payload },
  options,
) => {
  const { data, ...rest } = useQuery(
    queryKeys.timeOffRecords(canManageEmployee, payload),
    canManageEmployee
      ? async () => amApi.getManageTimeOffRecords({ ...payload })
      : async () => amApi.getProfileTimeOffRecords({ ...payload }),
    {
      ...options,
    },
  );
  return { records: data ?? [], ...rest };
};

export const useUserPolicy = (userId, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.userPolicy(userId),
    async () => amApi.getUserPolicy(userId),
    {
      ...options,
      enabled: !!userId,
    },
  );
  return { policies: data?.policies ?? [], ...rest };
};

export const useEmployeePolicyList = (employeeId, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.employeePolicyList(employeeId),
    async () => amApi.getEmployeePolicyList({ employeeId }),
    {
      ...options,
    },
  );
  return { policies: data ?? [], ...rest };
};

export const usePerformanceCycles = (options) => {
  const { data, ...rest } = useQuery(
    queryKeys.performanceCycles(),
    async () => amApi.getPerformanceCycles(),
    {
      ...options,
    },
  );
  return { performanceCycles: data ?? [], ...rest };
};

export const usePerformanceCycle = (id, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.performanceCycle(id),
    async () => amApi.getPerformanceCycle(id),
    {
      ...options,
    },
  );
  return { cycle: data, ...rest };
};

export const useSurveys = (params, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.surveys(params ? Object.values(params) : ['all']),
    async () => amApi.getSurveys(params),
    {
      ...options,
    },
  );
  return { surveys: data?.data ?? [], ...rest };
};

export const useSurvey = ({ id, canManageEmployee }, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.survey(id),
    async () =>
      canManageEmployee ? amApi.getSurvey(id) : amApi.getEmployeeSurvey(id),
    {
      ...options,
    },
  );
  return { survey: data, ...rest };
};

export const useUsers = ({ page, search }, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.users(page, search),
    async () => amApi.getUsers(formatUserFilterPayload({ search, page })),
    {
      ...options,
    },
  );

  return {
    users: data?.data ?? [],
    pagination: data?.pagination ?? null,
    ...rest,
  };
};

export const useGroupedPermissions = (options) => {
  const { data, ...rest } = useQuery(
    queryKeys.groupedPermissions(),
    async () => amApi.getGroupedPermissions(),
    {
      ...options,
    },
  );

  return {
    permissions: data?.data ?? [],
    ...rest,
  };
};

export const useUserTrainings = ({ userId, ...params }, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.userTrainings(userId),
    async () => amApi.getUserTrainings(userId, params),
    {
      ...options,
    },
  );

  return {
    trainings: data,
    ...rest,
  };
};

export const useUserRecords = ({ userId, ...params }, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.userRecords(userId, params),
    async () => amApi.getUserRecords(userId, params),
    {
      ...options,
    },
  );

  return {
    records: data?.data ?? [],
    years: data?.years?.map(String) ?? [],
    ...rest,
  };
};

export const useLoadJobTitles = (options) => {
  const { data, ...rest } = useQuery(
    queryKeys.jobTitles(),
    async () => amApi.getJobTitles(),
    { ...options },
  );

  return {
    jobTitles: data?.data ?? [],
    ...rest,
  };
};

export const useLoadDepartments = (options) => {
  const { data, ...rest } = useQuery(
    queryKeys.departments(),
    async () => amApi.getDepartments(),
    { ...options },
  );

  return {
    departments: data?.data ?? [],
    ...rest,
  };
};

export const useLoadLocations = (options) => {
  const { data, ...rest } = useQuery(
    queryKeys.locations(),
    async () => amApi.getLocations(),
    { ...options },
  );

  return {
    locations: data?.data ?? [],
    ...rest,
  };
};

export const useLoadDivisions = (options) => {
  const { data, ...rest } = useQuery(
    queryKeys.divisions(),
    async () => amApi.getDivisions(),
    { ...options },
  );

  return {
    divisions: data?.data ?? [],
    ...rest,
  };
};

export const useLoadJobInformationList = (id, options) => {
  const { data: jobInformationList, ...rest } = useQuery(
    queryKeys.jobInformationList(id),
    async () => amApi.getJobInformationList(id),
    { ...options },
  );

  return {
    jobInformationList: jobInformationList ?? [],
    ...rest,
  };
};

export const useLoadTaskList = (params, options) => {
  const { data: list, ...rest } = useQuery(
    queryKeys.tasksList(params),
    async () => amApi.getTasksList(params),
    { ...options },
  );

  return {
    list: list?.data ?? [],
    ...rest,
  };
};

export const useLoadTasks = (params, options) => {
  const { data: taskList, ...rest } = useQuery(
    queryKeys.tasks(params),
    async () => amApi.getTasks(params),
    { ...options },
  );

  return {
    tasksList: taskList?.data ?? [],
    ...rest,
  };
};

export const useLoadEmployeeBoardingList = (
  { userId, ...payload },
  options,
) => {
  const { data: onboardingList, ...rest } = useQuery(
    queryKeys.employeeBoardingList(userId, payload),
    async () => amApi.getEmployeeBoardingList(userId, payload),
    { ...options },
  );

  return {
    onboardingList: onboardingList?.data ?? [],
    employeeHireDate: onboardingList?.employee_hire_date ?? undefined,
    offboardingDate: onboardingList?.offboarding_date ?? null,
    ...rest,
  };
};

export const useCandidateSources = (payload, options) => {
  const { data: candidateSource, ...rest } = useQuery(
    queryKeys.candidateSource(),
    async () => amApi.getCandidateSource(payload),
    { ...options },
  );

  return {
    candidateSource: candidateSource ?? [],
    ...rest,
  };
};

export const useCandidateStatuses = (payload, options) => {
  const { data: candidateStatuses, ...rest } = useQuery(
    queryKeys.candidateStatuses(),
    async () => amApi.getCandidateStatuses(payload),
    { ...options },
  );

  return {
    candidateStatuses: candidateStatuses?.data ?? [],
    types: candidateStatuses?.types,
    ...rest,
  };
};

export const useLoadEmailTemplates = (options) => {
  const { data: emailTemplates, ...rest } = useQuery(
    queryKeys.emailTemplates(),
    async () => amApi.getEmailTemplates(),
    { ...options },
  );

  return {
    emailTemplates: emailTemplates ?? [],
    ...rest,
  };
};

export const useLoadOfferTemplates = (options) => {
  const { data: offerTemplates, ...rest } = useQuery(
    queryKeys.offerTemplates(),
    async () => amApi.getOfferTemplates(),
    { ...options },
  );

  return {
    offerTemplates: offerTemplates ?? [],
    ...rest,
  };
};

export const useLoadCandidateOffer = (hash, options) => {
  const { data: candidateOffer, ...rest } = useQuery(
    queryKeys.candidateOffer(hash),
    async () => amApi.getCandidateOffer(hash),
    { ...options },
  );

  return {
    candidateOffer: candidateOffer ?? {},
    ...rest,
  };
};

export const useLoadTemplateVariables = (options) => {
  const { data: templateVariables, ...rest } = useQuery(
    queryKeys.templateVariables(),
    async () => amApi.getTemplatesVariables(),
    { ...options },
  );

  return {
    templateVariables: templateVariables ?? {},
    ...rest,
  };
};

export const useLoadRequest = ({ id, canManageEmployee }, options) => {
  const { data: request, ...rest } = useQuery(
    canManageEmployee
      ? queryKeys.manageRequest(id)
      : queryKeys.employeeRequest(id),
    async () =>
      canManageEmployee
        ? amApi.getManageRequest(id)
        : amApi.getEmployeeRequest(id),
    { ...options },
  );

  return {
    request: request ?? {},
    ...rest,
  };
};

export const useLoadDashboardInfo = (options) => {
  const { data: dashboardInfo, ...rest } = useQuery(
    queryKeys.dashboardInfo(),
    async () => amApi.getDashboardInfo(),
    { ...options },
  );

  return {
    dashboardInfo: dashboardInfo ?? {},
    ...rest,
  };
};

export const useLoadLookups = (params, options) => {
  const { data: lookups, ...rest } = useQuery(
    queryKeys.lookups(params),
    async () => amApi.getLookups(params),
    { ...options },
  );

  return {
    lookups: lookups ?? {},
    ...rest,
  };
};

export const useLoadHeadCountReport = (params, options) => {
  const { data: headCountReport, ...rest } = useQuery(
    queryKeys.headCountReport(params),
    async () => amApi.getHeadCountReport(params),
    { ...options },
  );

  const employeesPayload = headCountReport?.employees;
  const employeesData = Array.isArray(employeesPayload)
    ? employeesPayload
    : (employeesPayload?.data ?? []);
  const employeesPagination = Array.isArray(employeesPayload)
    ? null
    : (employeesPayload?.pagination ?? null);

  return {
    headCountStats: headCountReport?.statistics ?? {},
    headCountList: employeesData,
    pagination: employeesPagination,
    ...rest,
  };
};

export const useLoadPolicy = (id, options) => {
  const { data: policy, ...rest } = useQuery(
    queryKeys.policy(id),
    async () => amApi.getPolicy(id),
    { ...options },
  );

  return {
    policy: policy ?? {},
    ...rest,
  };
};

export const useCheckIsValidPasswordResetToken = (token, options) => {
  const { ...rest } = useQuery(
    queryKeys.isValidResetPasswordToken(token),
    async () => amApi.checkIsValidPasswordResetToken(token),
    { ...options },
  );

  return {
    ...rest,
  };
};

export const useLoadEmployeeStatuses = (options) => {
  const { data: statuses, ...rest } = useQuery(
    queryKeys.employeeStatuses(),
    async () => amApi.getEmployeeStatuses(),
    { ...options },
  );

  return {
    statuses: statuses?.data ?? [],
    ...rest,
  };
};

export const useLoadEmployeeStatusRecords = (employeeId, options) => {
  const { data: records, ...rest } = useQuery(
    queryKeys.employeeStatusRecords(employeeId),
    async () => amApi.getEmployeeStatusRecords(employeeId),
    { ...options },
  );

  return {
    records: records ?? [],
    ...rest,
  };
};

export const useLoadReasons = ({ params }, options) => {
  const { data: reasons, ...rest } = useQuery(
    queryKeys.reasons(params),
    async () => amApi.getReasons(params),
    { ...options },
  );

  return {
    reasons: reasons?.data ?? [],
    ...rest,
  };
};

export const useLoadPaySchedule = ({ withoutTrashed }, options) => {
  const { data: paySchedules, ...rest } = useQuery(
    queryKeys.paySchedules(withoutTrashed),
    async () => amApi.getPaySchedules(withoutTrashed),
    { ...options },
  );

  return {
    paySchedules: paySchedules?.data ?? [],
    ...rest,
  };
};

export const useLoadEmployeeCompensationRecords = ({ employeeId }, options) => {
  const { data: employeeCompensationRecords, ...rest } = useQuery(
    queryKeys.employeeCompensationRecords(employeeId),
    async () => amApi.getEmployeeCompensationRecords(employeeId),
    { ...options },
  );

  return {
    employeeCompensationRecords: employeeCompensationRecords ?? [],
    ...rest,
  };
};

export const useLoadEmployeeOrgTree = (options) => {
  const { data: employeeOrgTree, ...rest } = useQuery(
    queryKeys.employeeOrgTree(),
    async () => amApi.getEmployeeOrgTree(),
    { ...options },
  );

  return {
    employeeOrgTree: employeeOrgTree ?? [],
    ...rest,
  };
};

export const useLoadEmployeeBonusRecords = ({ employeeId }, options) => {
  const { data: employeeBonusRecords, ...rest } = useQuery(
    queryKeys.employeeBonusRecords(employeeId),
    async () => amApi.getEmployeeBonusRecords(employeeId),
    { ...options },
  );

  return {
    employeeBonusRecords: employeeBonusRecords ?? [],
    ...rest,
  };
};

export const useLoadEmployeeAssessmentSurveys = (
  { canManageEmployee, employeeId, ...payload },
  options,
) => {
  const { data: employeeAssessmentSurveys, ...rest } = useQuery(
    queryKeys.employeeAssessmentSurveys(employeeId, payload),
    async () =>
      canManageEmployee
        ? amApi.getEmployeeAssessmentSurveys(employeeId, payload)
        : amApi.getEmployeeAssessmentSurveysForEmployeeRole(
            employeeId,
            payload,
          ),
    { ...options },
  );

  return {
    employeeAssessmentSurveys: employeeAssessmentSurveys ?? {},
    ...rest,
  };
};

export const useLoadTotalEmployeesByJobInfoFilter = (
  { ...filters },
  options,
) => {
  const { data, ...rest } = useQuery(
    queryKeys.totalEmployeesByJobInfoFilter(filters),
    async () => amApi.getTotalEmployeesByJobInfoFilter(filters),
    { ...options },
  );

  return {
    employeeCount: data?.count ?? 0,
    ...rest,
  };
};

export const useLoadAssetCategories = (options) => {
  const { data: assetCategories, ...rest } = useQuery(
    queryKeys.assetCategories(),
    async () => amApi.getAssetCategories(),
    { ...options },
  );

  return {
    assetCategories: assetCategories?.data ?? [],
    ...rest,
  };
};

export const useLoadEmployeeAssets = (employeeId, options) => {
  const { data: assets, ...rest } = useQuery(
    queryKeys.employeeAssets(employeeId),
    async () => amApi.getEmployeeAssets(employeeId),
    { ...options },
  );

  return {
    assets: assets ?? [],
    ...rest,
  };
};

export const useLoadEmployeeSubordinates = (employeeId, options) => {
  const subordinateIds = options?.subordinate_ids;

  const { data: subordinates, ...rest } = useQuery(
    queryKeys.employeeSubordinates(employeeId, subordinateIds),
    async () => {
      if (subordinateIds && subordinateIds.length > 0) {
        return amApi.getAllEmployeesByIds(subordinateIds);
      }
      return amApi.getEmployeeSubordinates(employeeId);
    },
    { ...options },
  );

  return {
    subordinates:
      subordinateIds && subordinateIds.length > 0
        ? subordinates || []
        : (subordinates?.children ?? []),
    ...rest,
  };
};

export const useCheckIsValidUserProviderToken = (provider, options) => {
  const data = useQuery(
    queryKeys.userTokenProvider(provider),
    async () => amApi.checkIsValidUserProviderToken(provider),
    { ...options },
  );

  return {
    ...data,
  };
};

export const useLoadCompanyIntegrations = (options, companyId = 1) => {
  const { data: companyIntegrations, ...rest } = useQuery(
    queryKeys.companyActiveIntegrations(companyId),
    async () => amApi.getCompanyActiveIntegrations(companyId),
    { ...options },
  );

  return {
    companyIntegrations: companyIntegrations?.data ?? [],
    ...rest,
  };
};

export const useLoadIntegrations = (options) => {
  const { data: integrations, ...rest } = useQuery(
    queryKeys.integrations(),
    async () => amApi.getIntegrations(),
    { ...options },
  );

  return {
    integrations: integrations ?? [],
    ...rest,
  };
};

export const useLoadBuildVersion = (options) => {
  const { data: buildVersion, ...rest } = useQuery(
    queryKeys.buildVersion(),
    async () => amApi.getBuildVersion(),
    { ...options },
  );

  storeGeneralSettings(buildVersion?.settings ?? {});

  return {
    buildVersion: buildVersion ?? '',
    ...rest,
  };
};

export const useLoadRoles = (payload, options) => {
  const { data: roles, ...rest } = useQuery(
    queryKeys.allRoles(payload),
    async () => amApi.getRoles(payload),
    { ...options },
  );

  return {
    roles: roles ?? [],
    ...rest,
  };
};

export const useLoadCompensationGroups = (payload, options) => {
  const { data: compensationGroups, ...rest } = useQuery(
    queryKeys.compensationGroups(),
    async () => amApi.getCompensationGroups(payload),
    { ...options },
  );

  return {
    compensationGroups: compensationGroups ?? [],
    ...rest,
  };
};

export const useLoadCalendarData = (payload, options) => {
  const { data: calendarData, ...rest } = useQuery(
    queryKeys.calendarData(payload),
    async () => amApi.getCalendarData(payload),
    { ...options },
  );

  return {
    calendarData: calendarData ?? [],
    ...rest,
  };
};

export const useLoadCalendarTypes = (options) => {
  const { data: eventTypes, ...rest } = useQuery(
    queryKeys.eventTypes(),
    async () => amApi.getCalendarEventTypes(),
    { ...options },
  );

  return {
    eventTypes: eventTypes ?? [],
    ...rest,
  };
};

export const useLoadTimeOffBalance = ({ employeeId, ...payload }, options) => {
  const params = {
    ...payload,
    include_overlaps: true,
  };

  const { data: timeOffBalance, ...rest } = useQuery(
    queryKeys.timeOffBalance(params),
    async () => amApi.getTimeOffBalanceByEmployeeId(employeeId, params),
    { ...options, enabled: !!employeeId },
  );
  return {
    timeOffBalance,
    ...rest,
  };
};

export const useLoadHolidays = (payload, options) => {
  const { data: holidays, ...rest } = useQuery(
    queryKeys.holidays(payload),
    async () => amApi.getHolidays(payload),
    { ...options },
  );

  return {
    holidays: holidays?.data ?? [],
    canCopyHolidays: holidays?.canCopy ?? false,
    ...rest,
  };
};

export const useLoadWidgets = (options) => {
  const { data: widgets, ...rest } = useQuery(
    queryKeys.widgets(),
    async () => amApi.getWidgets(),
    { ...options },
  );

  return {
    widgets: widgets ?? [],
    ...rest,
  };
};

export const useLoadEmployeeHolidays = (
  { employeeId, ...payload },
  options,
) => {
  const { data: holidays, ...rest } = useQuery(
    queryKeys.employeeHolidays({ employeeId, ...payload }),
    async () => amApi.getEmployeeHolidays(employeeId, payload),
    { ...options },
  );

  return {
    holidays: holidays ?? [],
    ...rest,
  };
};

export const useLoadCandidateOffers = (payload, options) => {
  const { data: candidateOffers, ...rest } = useQuery(
    queryKeys.candidatesOffers(payload),
    async () => amApi.getCandidatesOffers(payload),
    { ...options },
  );

  return {
    candidateOffers: candidateOffers ?? [],
    ...rest,
  };
};

export const useLoadCompanyLinks = (options) => {
  const { data: companyLinks, ...rest } = useQuery(
    queryKeys.companyLinks(),
    async () => amApi.getCompanyLinks(),
    { ...options },
  );

  return {
    companyLinks: companyLinks?.data ?? [],
    ...rest,
  };
};

export const useLoadCompanyLinksCategories = (options) => {
  const { data: companyLinksCategories, ...rest } = useQuery(
    queryKeys.companyLinksCategories(),
    async () => amApi.getCompanyLinksCategories(),
    { ...options },
  );

  return {
    companyLinksCategories: companyLinksCategories ?? [],
    ...rest,
  };
};

export const useLoadAnnouncements = (
  { hasManageEmployee, ...payload },
  options,
) => {
  const { data: announcements, ...rest } = useQuery(
    queryKeys.announcements(payload),
    async () =>
      hasManageEmployee
        ? amApi.getAnnouncements(payload)
        : amApi.getMyAnnouncements(payload),
    { ...options },
  );

  return {
    announcements: announcements?.data ?? [],
    pagination: announcements?.pagination ?? {},
    ...rest,
  };
};

export const useLoadAnnouncement = (
  { announcementId, hasManageEmployee },
  options,
) => {
  const { data: announcement, ...rest } = useQuery(
    queryKeys.announcement(announcementId),
    async () =>
      hasManageEmployee
        ? amApi.getAnnouncement(announcementId)
        : amApi.getEmployeeAnnouncement(announcementId),
    { ...options },
  );

  return {
    announcement: announcement?.data ?? {},
    ...rest,
  };
};

export const useLoadJobsOpening = (params, options) => {
  const { data: jobOpenings, ...rest } = useQuery(
    queryKeys.jobOpenings(params),
    async () => amApi.getJobOpenings(params),
    { ...options },
  );

  return {
    jobOpenings: jobOpenings?.data ?? [],
    pagination: jobOpenings?.pagination ?? {},
    ...rest,
  };
};

export const useLoadJobOpening = (id, options) => {
  const { data: jobOpening, ...rest } = useQuery(
    queryKeys.jobOpening(id),
    async () => amApi.getJobOpening(id),
    { ...options },
  );

  return {
    jobOpening: jobOpening ?? {},
    ...rest,
  };
};

export const useLoadRequestsCount = ({ ...params }, options) => {
  const { data: requestCounts, ...rest } = useQuery(
    queryKeys.requestsCount({ ...params }),
    async () => amApi.getRequestsCount(params),
    { ...options },
  );

  return {
    requestCounts: requestCounts ?? {},
    ...rest,
  };
};

export const useLoadSatisfactionSurvey = (options) => {
  const { data: satisfactionSurvey, ...rest } = useQuery(
    queryKeys.satisfactionSurvey(),
    async () => amApi.getSatisfactionSurvey(),
    { ...options },
  );

  return {
    satisfactionSurvey: satisfactionSurvey ?? {},
    ...rest,
  };
};

export const useLoadSatisfactionSurveyByHash = (surveyHash, options) => {
  const { data: satisfactionSurvey, ...rest } = useQuery(
    queryKeys.satisfactionSurveyByHash(surveyHash),
    async () => amApi.getSatisfactionSurveyByHash(surveyHash),
    { ...options },
  );

  return {
    satisfactionSurvey: satisfactionSurvey ?? {},
    ...rest,
  };
};

export const useLoadSatisfactionDates = (options) => {
  const { data, ...rest } = useQuery(
    queryKeys.satisfactionDates(),
    async () => amApi.getSatisfactionDates(),
    { ...options },
  );

  const intervals = [...(data?.available_intervals ?? [])];

  return {
    dates:
      _.reverse(intervals)?.map((i) =>
        moment(i, 'YYYY-MM').format('MMMM YYYY'),
      ) ?? [],
    ...rest,
  };
};

export const useLoadSatisfactionInterval = (payload, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.satisfactionInterval(payload),
    async () => amApi.getSatisfactionInterval(payload),
    { ...options },
  );

  return {
    interval: data?.data ?? [],
    ...rest,
  };
};

export const useLoadSatisfactionResponses = (payload, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.satisfactionResponses(payload),
    async () => amApi.getSatisfactionResponses(payload),
    { ...options },
  );

  return {
    responses: data?.data ?? {},
    ...rest,
  };
};

export const useLoadSurveyUpcomingDates = (payload, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.satisfactionSurveyUpcomingDates(payload),
    async () => amApi.getSatisfactionSurveyUpcomingDate(payload),
    { ...options },
  );

  return {
    upcomingDates: data ?? [],
    ...rest,
  };
};

export const useLoadPayrollReportData = (payload, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.payrollReportData(payload),
    async () => amApi.getPayrollReportData(payload),
    { ...options },
  );

  return {
    payroll: data ?? [],
    ...rest,
  };
};

export const useLoadTimeOffUsageData = (payload, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.timeOffUsageData(payload),
    async () => amApi.getTimeOffUsageData(payload),
    { ...options },
  );

  return {
    itemsData: data ?? [],
    ...rest,
  };
};

export const useLoadSalaryHistory = (payload, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.salaryHistory(payload),
    async () => amApi.getSalaryHistory(payload),
    { ...options },
  );

  return {
    itemsData: data ?? [],
    ...rest,
  };
};

export const useLoadAudit = (payload, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.audit(payload),
    async () => amApi.getAudit(payload),
    { ...options },
  );

  return {
    itemsData: data ?? [],
    ...rest,
  };
};

export const useLoadAuditFilters = (payload, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.auditFilters(payload),
    async () => amApi.getAuditFilters(payload),
    { ...options },
  );

  return {
    filters: data?.filters ?? [],
    ...rest,
  };
};

export const useLoadEmployeeTimesheetRecords = (payload, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.employeeTimesheetRecords(payload),
    async () => amApi.getEmployeeTimesheetRecords(payload),
    { ...options },
  );

  return {
    data,
    ...rest,
  };
};

export const useLoadTimeTrackingGroups = (options) => {
  const { data, ...rest } = useQuery(
    queryKeys.timeTrackingGroups(),
    async () => amApi.getTimeTrackingGroups(),
    { ...options },
  );

  return {
    groups: data?.data ?? [],
    ...rest,
  };
};

export const useLoadPayrolls = (payload, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.loadPayrolls(payload),
    async () => amApi.getPayrolls(payload),
    { ...options },
  );

  return {
    payrolls: data?.data ?? [],
    minYear: data?.first_record_year,
    ...rest,
  };
};

export const useLoadPayrollReport = (params, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.loadPayrollReport(params),
    async () => amApi.getPayrollReport(params),
    { ...options },
  );

  return {
    payroll: data ?? {},
    ...rest,
  };
};

export const useLoadHirePackets = (options) => {
  const { data, ...rest } = useQuery(
    queryKeys.hirePackets(),
    async () => amApi.getHirePackets(),
    { ...options },
  );

  return {
    hirePackets: data?.data ?? [],
    ...rest,
  };
};

export const useLoadHirePacketVariables = (options) => {
  const { data: hirePacketVariables, ...rest } = useQuery(
    queryKeys.hirePacketVariables(),
    async () => amApi.getHirePacketVariables(),
    { ...options },
  );

  return {
    hirePacketVariables: hirePacketVariables ?? {},
    ...rest,
  };
};

export const useLoadWorkPatterns = (options) => {
  const { data: workPatterns, ...rest } = useQuery(
    queryKeys.workPatterns(),
    async () => amApi.getWorkPatterns(),
    { ...options },
  );

  return {
    workPatterns: workPatterns?.data ?? [],
    ...rest,
  };
};

export const useLoadEmployeeWorkPatterns = (employeeId, options) => {
  const { data: employeeWorkPatterns, ...rest } = useQuery(
    queryKeys.employeeWorkPatterns(employeeId),
    async () => amApi.getEmployeeWorkPatterns(employeeId),
    { ...options },
  );

  return {
    employeeWorkPatterns: employeeWorkPatterns ?? [],
    ...rest,
  };
};

export const useInfiniteSearchItems = (query, options) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    ...rest
  } = useInfiniteQuery(
    queryKeys.searchItems(query),
    ({ pageParam = 1 }) => amApi.searchItems(query, pageParam),
    {
      enabled: !!query && query.length > 1,
      getNextPageParam: (requestData) => {
        const isNextPage =
          (requestData?.data?.length || 0) +
            (requestData?.limit || 0) * ((requestData?.page || 0) - 1) <
          (requestData?.total || 0);
        return isNextPage ? requestData.page + 1 : undefined;
      },
      cacheTime: 0,
      staleTime: 0,
      ...options,
    },
  );

  // Combine all pages of results
  const flattenedData = data?.pages.reduce(
    (acc, page) => ({
      data: [...acc.data, ...(page?.data || [])],
      total: page?.total || 0,
      page: page?.page || 1,
      limit: page?.limit || 10,
    }),
    { data: [], total: 0, page: 1, limit: 10 },
  );

  return {
    items: flattenedData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    ...rest,
  };
};

export const useLoadEmployeeExtraPayRecords = ({ employeeId }, options) => {
  const { data: employeeExtraPayRecords, ...rest } = useQuery(
    queryKeys.employeeExtraPayRecords(employeeId),
    async () => amApi.getEmployeeExtraPayRecords(employeeId),
    { ...options },
  );

  return {
    employeeExtraPayRecords: employeeExtraPayRecords?.data ?? [],
    ...rest,
  };
};

export const useLoadExtraPayByPayroll = ({ payrollId }, options) => {
  const { data: extraPayByPayroll, ...rest } = useQuery(
    queryKeys.extraPayByPayroll(payrollId),
    async () => amApi.getExtraPayByPayroll(payrollId),
    { ...options, enabled: !!payrollId && options?.enabled !== false },
  );

  return {
    extraPayByPayroll: extraPayByPayroll?.data ?? [],
    ...rest,
  };
};

export const useEmployeePayslips = ({ userId, params }, options) => {
  const { data: payslips, ...rest } = useQuery(
    queryKeys.employeePayslips(userId, params),
    async () => amApi.getEmployeePayslips(userId, params),
    { ...options, enabled: !!userId && options?.enabled !== false },
  );

  return {
    payslips: payslips?.data ?? [],
    pagination: payslips?.pagination ?? {},
    ...rest,
  };
};

export const useEmployeeExtraPays = ({ userId, fromDate, toDate }, options) => {
  const { data: extraPays, ...rest } = useQuery(
    queryKeys.employeeExtraPays(userId, fromDate, toDate),
    async () => amApi.getEmployeeExtraPays(userId, fromDate, toDate),
    {
      ...options,
      enabled: !!userId && !!fromDate && !!toDate && options?.enabled !== false,
    },
  );

  return {
    extraPays: extraPays?.data ?? [],
    ...rest,
  };
};

export const useEmployeeAllowances = ({ userId }, options) => {
  const { data: allowances, ...rest } = useQuery(
    queryKeys.employeeAllowances(userId),
    async () => amApi.getEmployeeAllowances(userId),
    { ...options, enabled: !!userId && options?.enabled !== false },
  );

  return {
    allowances: allowances?.data ?? [],
    ...rest,
  };
};

export const useEmployeeDeductions = ({ userId }, options) => {
  const { data: deductions, ...rest } = useQuery(
    queryKeys.employeeDeductions(userId),
    async () => amApi.getEmployeeDeductions(userId),
    { ...options, enabled: !!userId && options?.enabled !== false },
  );

  return {
    deductions: deductions?.data ?? [],
    ...rest,
  };
};

export const useTaxBands = (params, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.taxBands(params),
    async () => amApi.getTaxBands(params),
    {
      ...options,
    },
  );

  return {
    taxBands: data?.data ?? [],
    canCopyTaxBands: data?.canCopy ?? false,
    ...rest,
  };
};

export const useTaxBand = (id, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.taxBand(id),
    async () => amApi.getTaxBand(id),
    {
      ...options,
      enabled: !!id && options?.enabled !== false,
    },
  );

  return {
    taxBand: data,
    ...rest,
  };
};

export const useEmployeeWithAllowancesDeductions = (params, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.employeeAllowancesDeductions(params),
    async () => amApi.getEmployeeAllowancesDeductions(params),
    options,
  );

  return {
    employees: data ?? [],
    ...rest,
  };
};

export const useAllowances = (params, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.allowances(params),
    async () => amApi.getAllowances(params),
    options,
  );

  return {
    allowances: data?.data ?? [],
    canCopy: data?.canCopy ?? false,
    ...rest,
  };
};

export const useAllowance = (id, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.allowance(id),
    async () => amApi.getAllowance(id),
    {
      ...options,
      enabled: !!id && options?.enabled !== false,
    },
  );

  return {
    allowance: data,
    ...rest,
  };
};

export const useDeductions = (params, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.deductions(params),
    async () => amApi.getDeductions(params),
    options,
  );

  return {
    deductions: data?.data ?? [],
    canCopy: data?.canCopy ?? false,
    ...rest,
  };
};

export const useDeduction = (id, options) => {
  const { data, ...rest } = useQuery(
    queryKeys.deduction(id),
    async () => amApi.getDeduction(id),
    {
      ...options,
      enabled: !!id && options?.enabled !== false,
    },
  );

  return {
    deduction: data,
    ...rest,
  };
};
